import { shallowCompareDictionaries } from "@configura/web-utilities";
/**
 * Makes a string from the params which can be used as a key in for example React. Language is not respected.
 */
export const makeCatalogueKey = (cat) => `${cat.cid}-${cat.enterprise}-${cat.prdCat}-${cat.prdCatVersion}-${cat.priceList}-${cat.vendor}`;
/**
 * Makes a string from the params which can be used as a key in for example React. Language is not respected.
 */
export const makeProductKey = (prod) => `${makeCatalogueKey(prod)}-${prod.partNumber}`;
/**
 * Makes a string from selected options, can be used as a key for a selection.
 */
export const makeSelOptionsKey = (options) => options.reduce((p, option) => {
    var _a;
    const { code, numericValue, next } = option;
    p += `_{${code}`;
    p += numericValue === undefined ? "" : `_${numericValue.value}${(_a = numericValue.unit) !== null && _a !== void 0 ? _a : ""}`;
    if (next === undefined) {
        return p;
    }
    for (const key of Object.keys(next)) {
        const innerOption = next[key];
        p += `_{${key}_${makeSelOptionsKey([innerOption])}_}`;
    }
    p += "_}";
    return p;
}, "");
/**
 * Deep compare. Uses the JSON.stringify method for comparison
 * @returns true if equal. Key and array order is respected.
 */
export function compareCfgProductData(pd1, pd2) {
    return JSON.stringify(pd1) === JSON.stringify(pd2);
}
export function isModel(arg) {
    return typeof arg === "object" && arg !== null && "cid" in arg && "uri" in arg;
}
/** Replace empty strings with "-" for compatibility with the API. */
export function correctDefaultsOnCatalogueParams(catId) {
    // Enterprise, prdCat and vendor have to be set, so we don't try and fix those
    return Object.assign(Object.assign({}, catId), { prdCatVersion: catId.prdCatVersion || "-", priceList: catId.priceList || "-" });
}
export function recursivelyGetPriceCodeValue(priceCodes, prices) {
    while (prices) {
        for (const name of priceCodes) {
            const priceCodeUpcharge = prices === null || prices === void 0 ? void 0 : prices.values[name];
            if (priceCodeUpcharge !== undefined) {
                return priceCodeUpcharge;
            }
        }
        prices = prices.parent;
    }
    return undefined;
}
export function comparePricesObjects(prices1, prices2) {
    if (prices1 === undefined && prices2 === undefined) {
        return true;
    }
    if (prices1 === undefined || prices2 === undefined) {
        return false;
    }
    const parent1 = prices1.parent;
    const parent2 = prices2.parent;
    if (!comparePricesObjects(parent1, parent2)) {
        return false;
    }
    return shallowCompareDictionaries(prices1.values, prices2.values);
}
export const decodeCatalogueParams = (params) => (Object.assign(Object.assign({}, params), { enterprise: decodeURIComponent(params.enterprise), prdCat: decodeURIComponent(params.prdCat), prdCatVersion: decodeURIComponent(params.prdCatVersion), priceList: decodeURIComponent(params.priceList), vendor: decodeURIComponent(params.vendor) }));
export const decodeProductParams = (params) => (Object.assign(Object.assign({}, decodeCatalogueParams(params)), { partNumber: decodeURIComponent(params.partNumber) }));
export function isSameCatalogueParams(left, right) {
    return (left.enterprise === right.enterprise &&
        left.prdCat === right.prdCat &&
        left.prdCatVersion === right.prdCatVersion &&
        left.priceList === right.priceList &&
        left.vendor === right.vendor);
}
export function isSameVector(left, right) {
    return left.x === right.x && left.y === right.y && left.z === right.z;
}
export function isSameOrientation(left, right) {
    return left.pitch === right.pitch && left.roll === right.roll && left.yaw === right.yaw;
}
export function isSameAnchor(left, right) {
    var _a, _b;
    if (left.code !== right.code) {
        return false;
    }
    if (left.anchorPoint !== right.anchorPoint) {
        return false;
    }
    const leftMeasurePriorities = (_a = left.measurePriority) !== null && _a !== void 0 ? _a : [];
    const rightMeasurePriorities = (_b = right.measurePriority) !== null && _b !== void 0 ? _b : [];
    if (leftMeasurePriorities.length !== rightMeasurePriorities.length) {
        return false;
    }
    if (leftMeasurePriorities.some((p, i) => p.url !== rightMeasurePriorities[i].url)) {
        return false;
    }
    return true;
}
export function isSameTransform(left, right) {
    const leftTransformPos = left.pos;
    const rightTransformPos = right.pos;
    const leftTransformRot = left.rot;
    const rightTransformRot = right.rot;
    const leftTransformScale = left.scale;
    const rightTransformScale = right.scale;
    if ((leftTransformPos === undefined) !== (rightTransformPos === undefined)) {
        return false;
    }
    if ((leftTransformRot === undefined) !== (rightTransformRot === undefined)) {
        return false;
    }
    if ((leftTransformScale === undefined) !== (rightTransformScale === undefined)) {
        return false;
    }
    if (leftTransformPos !== undefined &&
        rightTransformPos !== undefined &&
        !isSameVector(leftTransformPos, rightTransformPos)) {
        return false;
    }
    if (leftTransformRot !== undefined &&
        rightTransformRot !== undefined &&
        !isSameOrientation(leftTransformRot, rightTransformRot)) {
        return false;
    }
    if (leftTransformScale !== undefined &&
        rightTransformScale !== undefined &&
        !isSameVector(leftTransformScale, rightTransformScale)) {
        return false;
    }
    return true;
}
export function isSameProductRef(left, right) {
    if (left.refKey !== right.refKey) {
        return false;
    }
    if (left.partNumber !== right.partNumber) {
        return false;
    }
    if (!isSameCatalogueParams(left.catId, right.catId)) {
        return false;
    }
    if (left.refDescription !== right.refDescription) {
        return false;
    }
    const leftAnchor = left.anchor;
    const rightAnchor = right.anchor;
    if ((leftAnchor === undefined) !== (rightAnchor === undefined)) {
        return false;
    }
    if (leftAnchor !== undefined &&
        rightAnchor !== undefined &&
        !isSameAnchor(leftAnchor, rightAnchor)) {
        return false;
    }
    const leftTransform = left.transform;
    const rightTransform = right.transform;
    if ((leftTransform === undefined) !== (rightTransform === undefined)) {
        return false;
    }
    if (leftTransform !== undefined &&
        rightTransform !== undefined &&
        !isSameTransform(leftTransform, rightTransform)) {
        return false;
    }
    if (left.optional !== right.optional) {
        return false;
    }
    return true;
}
export const isSameDtoCatalogueParamsWithLang = (left, right) => left.cid === right.cid &&
    left.lang === right.lang &&
    left.enterprise === right.enterprise &&
    left.prdCat === right.prdCat &&
    left.prdCatVersion === right.prdCatVersion &&
    left.priceList === right.priceList &&
    left.vendor === right.vendor;
export const isSameDtoProductParamsWithLang = (left, right) => isSameDtoCatalogueParamsWithLang(left, right) && left.partNumber === right.partNumber;
